import { useEffect, useState } from "react";
import mermaid from "mermaid";
import { cn } from "~/lib/utils";

// Lazy-init mermaid once per module load. mermaid.initialize is idempotent, so
// doing it at module scope is cheaper than and equivalent to an effect.
let mermaidReady = false;
function ensureMermaid() {
  if (mermaidReady) return;
  mermaid.initialize({ startOnLoad: false, theme: "default" });
  mermaidReady = true;
}

interface MermaidDiagramProps {
  // Mermaid source. Rendering is skipped while this is empty.
  source: string;
  // Distinguishes concurrently mounted diagrams, which mermaid keys by id.
  idPrefix: string;
  className?: string;
}

// Renders mermaid source as inline SVG.
//
// Shared by the artifact lineage tab, whose diagram is built by the backend,
// and the pinned-artifact workflow drawer, whose diagram is built from the
// frozen recipe in the browser. Only the source differs.
export const MermaidDiagram = ({ source, idPrefix, className }: MermaidDiagramProps) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    if (!source || !container) return;
    ensureMermaid();
    const diagramId = `${idPrefix}-${Date.now()}`;

    // Render into a fixed off-screen sandbox so mermaid's temporary DOM
    // insertion doesn't trigger the browser's scroll anchor algorithm and
    // cause the page to jump when the diagram appears.
    const sandbox = document.createElement("div");
    sandbox.style.cssText = "position:fixed;top:0;left:-9999px;visibility:hidden";
    document.body.appendChild(sandbox);

    let cancelled = false;
    mermaid
      .render(diagramId, source, sandbox)
      .then(({ svg }) => {
        if (cancelled) return;
        container.innerHTML = svg;
        setRenderError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRenderError(err instanceof Error ? err.message : "Failed to render diagram");
      })
      .finally(() => {
        if (document.body.contains(sandbox)) {
          document.body.removeChild(sandbox);
        }
      });
    return () => {
      cancelled = true;
      if (document.body.contains(sandbox)) {
        document.body.removeChild(sandbox);
      }
    };
  }, [source, idPrefix, container]);

  if (renderError) {
    return <p className="py-3 text-xs text-destructive">{renderError}</p>;
  }
  return (
    <div
      ref={setContainer}
      className={cn(
        "flex items-center justify-center overflow-x-auto rounded-sm border border-border bg-background p-4",
        className
      )}
    />
  );
};
